import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SustainabilityQuestionnaire.module.css';
import { apiRequest } from '../api/api';

function SustainabilityQuestionnaire() {
  const navigate = useNavigate();
  // Section 1: Brand Information
  const [brandName, setBrandName] = useState('');
  const [brandWebsite, setBrandWebsite] = useState('');
  const [contact, setContact] = useState('');
  const [hqLocation, setHqLocation] = useState('');
  const [manufacturingLocations, setManufacturingLocations] = useState('');
  const [certifications, setCertifications] = useState('');
  const [publishReport, setPublishReport] = useState('No');
  // Section 1: Materials & Ingredients
  const [materialsVegan, setMaterialsVegan] = useState('');
  const [materialComposition, setMaterialComposition] = useState('');
  const [materialsRecyclable, setMaterialsRecyclable] = useState('');
  const [materialsDisclosure, setMaterialsDisclosure] = useState('');
  // Section 2: Packaging & Waste Management
  const [packagingType, setPackagingType] = useState('');
  const [packagingMinimize, setPackagingMinimize] = useState('');
  const [packagingWasteProgram, setPackagingWasteProgram] = useState('');
  // Section 3: Sourcing & Supply Chain
  const [sourcingOrigin, setSourcingOrigin] = useState('');
  const [sourcingDisclosure, setSourcingDisclosure] = useState('');
  const [sourcingLogistics, setSourcingLogistics] = useState('');
  // Section 4: Ethical Labor Practices
  const [ethicalWages, setEthicalWages] = useState('');
  const [ethicalWorkforce, setEthicalWorkforce] = useState('');
  const [ethicalPolicies, setEthicalPolicies] = useState('');
  // Section 5: Renewable Energy & Carbon Footprint
  const [renewableEnergy, setRenewableEnergy] = useState('');
  const [carbonOffsets, setCarbonOffsets] = useState('');
  // Section 6: Community & Give Back
  const [communityInitiatives, setCommunityInitiatives] = useState('');
  const [communityEducation, setCommunityEducation] = useState('');
  // Section 7: Transparency & Certifications
  const [transparencyCertifications, setTransparencyCertifications] = useState('');
  const [transparencyReport, setTransparencyReport] = useState('');
  // Bonus: Workplace Sustainability
  const [bonusOfficePolicies, setBonusOfficePolicies] = useState('');
  const [bonusCarbonTracking, setBonusCarbonTracking] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const questionnaireData = {
      brandName,
      brandWebsite,
      contact,
      hqLocation,
      manufacturingLocations,
      certifications,
      publishReport,
      materialsVegan,
      materialComposition,
      materialsRecyclable,
      materialsDisclosure,
      packagingType,
      packagingMinimize,
      packagingWasteProgram,
      sourcingOrigin,
      sourcingDisclosure,
      sourcingLogistics,
      ethicalWages,
      ethicalWorkforce,
      ethicalPolicies,
      renewableEnergy,
      carbonOffsets,
      communityInitiatives,
      communityEducation,
      transparencyCertifications,
      transparencyReport,
      bonusOfficePolicies,
      bonusCarbonTracking
    };
    try {
      await apiRequest({ method: 'POST', url: '/questionnaire', data: questionnaireData });
    } catch (err) {
      console.error('Failed to submit questionnaire to server', err);
    }
    const userContent = `## Brand Information\n- Brand Name: ${brandName}\n- Brand Website: ${brandWebsite}\n- Point of Contact: ${contact}\n- Headquarters Location: ${hqLocation}\n- Manufacturing Locations: ${manufacturingLocations}\n- Certifications: ${certifications}\n- Publish Report: ${publishReport}\n\n## Section 1: Materials & Ingredients (20 Points)\n1. 100% plant-based & vegan: ${materialsVegan}\n2. Material Composition: ${materialComposition}\n3. Materials recyclable/compostable: ${materialsRecyclable}\n4. Public ingredient disclosure: ${materialsDisclosure}\n\n## Section 2: Packaging & Waste Management (15 Points)\n1. Packaging type: ${packagingType}\n2. Minimize packaging/refills: ${packagingMinimize}\n3. Waste management program: ${packagingWasteProgram}\n\n## Section 3: Sourcing & Supply Chain (15 Points)\n1. Raw material origin: ${sourcingOrigin}\n2. Public origin disclosure: ${sourcingDisclosure}\n3. Logistics practices: ${sourcingLogistics}\n\n## Section 4: Ethical Labor Practices (15 Points)\n1. Fair wages & safety: ${ethicalWages}\n2. Workforce diversity (%): ${ethicalWorkforce}\n3. Equity policies: ${ethicalPolicies}\n\n## Section 5: Renewable Energy & Carbon Footprint (10 Points)\n1. Renewable energy use: ${renewableEnergy}\n2. Carbon offsets: ${carbonOffsets}\n\n## Section 6: Community & Give Back (10 Points)\n1. Giveback initiatives: ${communityInitiatives}\n2. Customer education: ${communityEducation}\n\n## Section 7: Transparency & Certifications (5 Points)\n1. Certifications: ${transparencyCertifications}\n2. Annual report publication: ${transparencyReport}\n\n## Bonus: Workplace Sustainability\n1. Green office policies: ${bonusOfficePolicies}\n2. Track carbon footprint: ${bonusCarbonTracking}`;
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            { role: 'system', content: `You are a Suspire LLM evaluator. Use the Suspire Sustainability Score Evaluation System instructions (sections and scoring) to evaluate the following brand responses and produce a markdown report with:\n- Final Score (1–10)\n- Category-wise Score Breakdown table\n- Key Strengths\n- Areas for Improvement\n- Immediate and Long-Term Recommendations\n- Re-evaluation Opportunity\n\nUse the instructions exactly as defined in the Suspire Sustainability Score (SSS) Evaluation System.` },
            { role: 'user', content: userContent }
          ],
          max_tokens: 1000
        })
      });
      const data = await response.json();
      const aiMessage = data.choices[0].message.content;
      navigate('/3sc-certificate', { state: { certificate: aiMessage, questionnaireData } });
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles['questionnaire-page']}>
      <form onSubmit={handleSubmit} className={styles['questionnaire-form']}>
        <fieldset>
          <legend>Brand Information</legend>
          <label>
            Brand Name
            <input type="text" placeholder="Brand Name" value={brandName} onChange={(e) => setBrandName(e.target.value)} required />
          </label>
          <label>
            Brand Website
            <input type="url" placeholder="Brand Website" value={brandWebsite} onChange={(e) => setBrandWebsite(e.target.value)} required />
          </label>
          <label>
            Point of Contact (Name & Email)
            <input type="text" placeholder="Point of Contact (Name & Email)" value={contact} onChange={(e) => setContact(e.target.value)} required />
          </label>
          <label>
            Headquarters Location
            <input type="text" placeholder="Headquarters Location" value={hqLocation} onChange={(e) => setHqLocation(e.target.value)} />
          </label>
          <label>
            Manufacturing Locations
            <input type="text" placeholder="Manufacturing Locations" value={manufacturingLocations} onChange={(e) => setManufacturingLocations(e.target.value)} />
          </label>
          <label>
            Certifications (comma-separated)
            <input type="text" placeholder="Certifications (comma-separated)" value={certifications} onChange={(e) => setCertifications(e.target.value)} />
          </label>
          <label>
            Publish Report
            <select value={publishReport} onChange={(e) => setPublishReport(e.target.value)}>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </label>
        </fieldset>
        <fieldset>
          <legend>1. Materials & Ingredients</legend>
          <label>
            100% plant-based & vegan?
            <select value={materialsVegan} onChange={(e) => setMaterialsVegan(e.target.value)}>
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Partial">Partial</option>
            </select>
          </label>
          <label>
            Material Composition:
            <input type="text" value={materialComposition} onChange={(e) => setMaterialComposition(e.target.value)} />
          </label>
          <label>
            Materials recyclable/compostable?
            <select value={materialsRecyclable} onChange={(e) => setMaterialsRecyclable(e.target.value)}>
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Partial">Partial</option>
            </select>
          </label>
          <label>
            Public ingredient disclosure?
            <select value={materialsDisclosure} onChange={(e) => setMaterialsDisclosure(e.target.value)}>
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Partial">Partial</option>
            </select>
          </label>
        </fieldset>
        <fieldset>
          <legend>2. Packaging & Waste Management</legend>
          <label>
            Packaging type:
            <input type="text" value={packagingType} onChange={(e) => setPackagingType(e.target.value)} />
          </label>
          <label>
            Minimize packaging/refills?
            <select value={packagingMinimize} onChange={(e) => setPackagingMinimize(e.target.value)}>
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Partial">Partial</option>
            </select>
          </label>
          <label>
            Waste management program:
            <input type="text" value={packagingWasteProgram} onChange={(e) => setPackagingWasteProgram(e.target.value)} />
          </label>
        </fieldset>
        <fieldset>
          <legend>3. Sourcing & Supply Chain</legend>
          <label>
            Raw material origin:
            <input type="text" value={sourcingOrigin} onChange={(e) => setSourcingOrigin(e.target.value)} />
          </label>
          <label>
            Public origin disclosure?
            <select value={sourcingDisclosure} onChange={(e) => setSourcingDisclosure(e.target.value)}>
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Partial">Partial</option>
            </select>
          </label>
          <label>
            Logistics practices:
            <input type="text" value={sourcingLogistics} onChange={(e) => setSourcingLogistics(e.target.value)} />
          </label>
        </fieldset>
        <fieldset>
          <legend>4. Ethical Labor Practices</legend>
          <label>
            Fair wages & safety?
            <select value={ethicalWages} onChange={(e) => setEthicalWages(e.target.value)}>
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Partial">Partial</option>
            </select>
          </label>
          <label>
            Workforce diversity (%):
            <input type="number" value={ethicalWorkforce} onChange={(e) => setEthicalWorkforce(e.target.value)} />
          </label>
          <label>
            Equity policies:
            <input type="text" value={ethicalPolicies} onChange={(e) => setEthicalPolicies(e.target.value)} />
          </label>
        </fieldset>
        <fieldset>
          <legend>5. Renewable Energy & Carbon Footprint</legend>
          <label>
            Renewable energy use:
            <input type="text" value={renewableEnergy} onChange={(e) => setRenewableEnergy(e.target.value)} />
          </label>
          <label>
            Carbon offsets:
            <input type="text" value={carbonOffsets} onChange={(e) => setCarbonOffsets(e.target.value)} />
          </label>
        </fieldset>
        <fieldset>
          <legend>6. Community & Give Back</legend>
          <label>
            Giveback initiatives:
            <input type="text" value={communityInitiatives} onChange={(e) => setCommunityInitiatives(e.target.value)} />
          </label>
          <label>
            Customer education:
            <input type="text" value={communityEducation} onChange={(e) => setCommunityEducation(e.target.value)} />
          </label>
        </fieldset>
        <fieldset>
          <legend>7. Transparency & Certifications</legend>
          <label>
            Certifications:
            <input type="text" value={transparencyCertifications} onChange={(e) => setTransparencyCertifications(e.target.value)} />
          </label>
          <label>
            Annual report publication?
            <select value={transparencyReport} onChange={(e) => setTransparencyReport(e.target.value)}>
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Partial">Partial</option>
            </select>
          </label>
        </fieldset>
        <fieldset>
          <legend>Bonus: Workplace Sustainability</legend>
          <label>
            Green office policies:
            <input type="text" value={bonusOfficePolicies} onChange={(e) => setBonusOfficePolicies(e.target.value)} />
          </label>
          <label>
            Track carbon footprint:
            <input type="text" value={bonusCarbonTracking} onChange={(e) => setBonusCarbonTracking(e.target.value)} />
          </label>
        </fieldset>
        <button type="submit" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit for Evaluation'}</button>
      </form>
    </div>
  );
}

export default SustainabilityQuestionnaire;
